import { useEffect, useState, type ReactNode } from 'react';
import {
  formatDate,
  formatDateShortYear,
  formatInteger,
  formatMonthName,
  formatNumber,
  formatPercent,
  formatSigned,
  formatTime,
  TV_PROJECT_PAGE_SECONDS,
  TV_PROJECT_ROWS,
  type ProjectStatus,
  type TvProject,
  type TvProjectsResponse,
  type TvTopCustomers,
} from '@codepass/shared';
import { cn } from '@/lib/cn';
import { TvScreenPage } from '@/pages/TvDashboard';

/**
 * Storskaerm 2 - "Hvor står vores projekter?" Spec: docs/dashboard/Storskaerm-2-Spec.md, layout:
 * storskaerm-2-reference.html beside it, whose CSS is translated here one to one.
 *
 * Every number is computed in the API - see tv-projects.ts - and only formatted and drawn here.
 * More than `TV_PROJECT_ROWS` projects turn the page every `TV_PROJECT_PAGE_SECONDS` with a short
 * fade; the rows never shrink. No interaction.
 */

const RESPONSE_FIELDS = Object.keys({
  generated_at: true,
  updated_at: true,
  stale: true,
  display: true,
  projects: true,
  capacity: true,
  top_customers: true,
} satisfies Record<keyof TvProjectsResponse, true>);

export function TvProjectsPage() {
  return (
    <TvScreenPage<TvProjectsResponse>
      screen="projects"
      apiPath="/dashboard/tv/projects"
      fields={RESPONSE_FIELDS}
      className="bg-tv2-bg"
      render={(data, stale) => <TvProjectsView data={data} stale={stale} />}
    />
  );
}

export const STATUS: Record<ProjectStatus, { label: string; text: string; dot: string }> = {
  on_track: { label: 'På sporet', text: 'text-tv2-green', dot: 'bg-tv2-green' },
  attention: { label: 'Opmærksomhed', text: 'text-tv2-amber', dot: 'bg-tv2-amber' },
  behind: { label: 'Bagud', text: 'text-tv2-coral', dot: 'bg-tv2-coral' },
};

/** The columns of the rows and their headings - `--cols` in the reference. */
const COLUMNS =
  'minmax(0,2.2fr) minmax(0,2.1fr) minmax(0,1.3fr) minmax(0,1.2fr) minmax(0,0.8fr) minmax(0,1.25fr) minmax(0,0.4fr) minmax(0,1.6fr)';

/**
 * The page of rows shown now, turning every `seconds` while there is more than one. Starts over
 * when the number of pages shrinks below the one shown.
 */
export function usePage(
  count: number,
  size: number,
  seconds: number,
): { page: number; pages: number } {
  const pages = Math.max(1, Math.ceil(count / size));
  const [page, setPage] = useState(0);

  useEffect(() => {
    if (pages <= 1) return;
    const timer = window.setInterval(() => setPage((current) => current + 1), seconds * 1000);
    return () => window.clearInterval(timer);
  }, [pages, seconds]);

  return { page: page % pages, pages };
}

/** The status pills and the time of the numbers - amber when they are old (spec, Tilstande). */
export function TopBar({
  eyebrow,
  title,
  counts,
  updatedAt,
  stale,
}: {
  eyebrow: string;
  title: string;
  counts: Record<ProjectStatus, number>;
  updatedAt: string;
  stale: boolean;
}) {
  const order: ProjectStatus[] = ['on_track', 'attention', 'behind'];
  return (
    <div className="flex items-end justify-between">
      <div>
        <p className="text-[14px] font-semibold tracking-[3px] text-tv2-muted">{eyebrow}</p>
        <p className="mt-0.5 text-[32px] font-bold leading-none">{title}</p>
      </div>
      <div className="flex items-center gap-3">
        {order.map((status) => (
          <span
            key={status}
            className="flex items-center gap-2 rounded-full bg-tv2-surface px-3.5 py-1.5 text-[16px] font-medium"
          >
            <span
              className={cn('h-[11px] w-[11px] rounded-full', STATUS[status].dot)}
              aria-hidden
            />
            {counts[status]} {STATUS[status].label.toLowerCase()}
          </span>
        ))}
        <span
          className={cn(
            'ml-2 text-[14px]',
            stale ? 'font-semibold text-tv2-amber' : 'text-tv2-muted',
          )}
        >
          {stale ? 'Data ikke opdateret siden ' : 'Opdateret '}
          {formatDate(updatedAt)} · {formatTime(updatedAt)}
        </span>
      </div>
    </div>
  );
}

export function statusCounts(
  rows: ReadonlyArray<{ status: ProjectStatus }>,
): Record<ProjectStatus, number> {
  const counts: Record<ProjectStatus, number> = { on_track: 0, attention: 0, behind: 0 };
  for (const row of rows) counts[row.status] += 1;
  return counts;
}

export function TvProjectsView({ data, stale }: { data: TvProjectsResponse; stale: boolean }) {
  const { page, pages } = usePage(data.projects.length, TV_PROJECT_ROWS, TV_PROJECT_PAGE_SECONDS);
  const shown = data.projects.slice(page * TV_PROJECT_ROWS, (page + 1) * TV_PROJECT_ROWS);

  return (
    <div className="flex h-full flex-col gap-3 px-2">
      <TopBar
        eyebrow="PROJEKTER · STATUS"
        title="Hvor står vores projekter?"
        counts={statusCounts(data.projects)}
        updatedAt={data.updated_at}
        stale={stale}
      />

      <div
        className="grid gap-[18px] px-5 text-[12px] font-semibold tracking-[1.5px] text-tv2-muted"
        style={{ gridTemplateColumns: COLUMNS }}
      >
        <span>KONTRAKT · PROJEKT</span>
        <span>UDVIKLERE</span>
        <span>MILEPÆLE</span>
        <span>FREMDRIFT</span>
        <span>TEMPO</span>
        <span>FÆRDIG · MARGIN</span>
        <span>CR</span>
        <span>STATUS</span>
      </div>

      {/* The key changes with the page, so the fade runs on every turn. */}
      <div
        key={page}
        className="grid min-h-0 flex-1 animate-tvfade content-between"
        /* A place for every row of a full page, so a short list stands where a full one would. */
        style={{ gridTemplateRows: `repeat(${TV_PROJECT_ROWS}, 52px)` }}
      >
        {shown.length === 0 ? (
          <p className="p-5 text-[15px] text-tv2-soft">Ingen aktive projekter</p>
        ) : (
          shown.map((project) => (
            <ProjectRow key={`${project.contract_no}-${project.name}`} project={project} />
          ))
        )}
      </div>

      <div className="flex items-center gap-6 px-5 text-[12px] text-tv2-muted">
        <span className="flex items-center gap-2">
          <i className="inline-block h-[13px] w-[13px] rounded-[3px] bg-tv2-green" />
          Milepæl lukket
        </span>
        <span className="flex items-center gap-2">
          <i className="inline-block h-[13px] w-[13px] rounded-[3px] border-[3px] border-tv2-green" />
          I gang
        </span>
        {pages > 1 ? (
          <span className="ml-auto">
            Side {page + 1} af {pages}
          </span>
        ) : null}
      </div>

      <div className="grid h-[210px] shrink-0 grid-cols-[minmax(0,1fr)_minmax(0,2.5fr)_minmax(0,1.35fr)_minmax(0,1.15fr)] gap-4">
        <RoomBox capacity={data.capacity} />
        <WeeksBox capacity={data.capacity} />
        <UnassignedBox unassigned={data.capacity.unassigned} />
        <TopCustomersBox top={data.top_customers} />
      </div>
    </div>
  );
}

/** "±0,0 t", "Opstart" before anything is closed, else signed with one decimal. */
function deviationText(project: TvProject): string {
  const value = project.plan_deviation_hours;
  if (value === null) return '';
  if (Math.abs(value) < 0.05) {
    return project.milestones.includes('closed') ? `±${formatNumber(0, 1)} t` : 'Opstart';
  }
  return `${formatSigned(value, 1)} t`;
}

function ProjectRow({ project }: { project: TvProject }) {
  const firstOpen = project.milestones.indexOf('open');
  const status = STATUS[project.status];
  const deviation = project.plan_deviation_hours;
  const margin = project.margin_days;

  return (
    <div
      className="grid h-[52px] shrink-0 items-center gap-[18px] rounded-lg border-t-2 border-tv2-accent bg-tv2-surface px-5"
      style={{ gridTemplateColumns: COLUMNS }}
    >
      <div className="flex min-w-0 items-baseline gap-3">
        <span className="whitespace-nowrap text-[14px] font-semibold tracking-[1px] text-tv2-teal">
          {project.contract_no}
        </span>
        <span className="truncate text-[20px] font-semibold">{project.name}</span>
      </div>
      <div className="min-w-0">
        {project.developers.length > 0 ? (
          <p className="truncate text-[18px] font-semibold">{project.developers.join(', ')}</p>
        ) : (
          <p className="text-[18px] font-semibold text-tv2-amber">Ikke tildelt</p>
        )}
      </div>
      <div className="flex gap-1 overflow-hidden">
        {project.milestones.map((milestone, index) => (
          <span
            key={index}
            className={cn(
              'h-3 w-3 shrink-0 rounded-[3px]',
              milestone === 'closed'
                ? 'bg-tv2-green'
                : index === firstOpen
                  ? 'border-2 border-tv2-green'
                  : 'bg-tv2-bg',
            )}
          />
        ))}
      </div>
      <div className="flex items-baseline gap-3 whitespace-nowrap">
        <span className="tabular text-[20px] font-bold text-tv2-green">
          {formatPercent(project.progress_pct)}
        </span>
        <span
          className={cn(
            'tabular text-[15px] font-semibold',
            deviation !== null && deviation < 0 ? 'text-tv2-coral' : 'text-tv2-green',
          )}
        >
          {deviationText(project)}
        </span>
      </div>
      <p className="tabular whitespace-nowrap text-[18px] font-semibold">
        {formatNumber(project.pace_hours_per_week, 1)}{' '}
        <span className="text-[13px] font-normal text-tv2-muted">t/uge</span>
      </p>
      <div className="flex items-baseline gap-2.5 whitespace-nowrap">
        <b className="tabular text-[18px] font-semibold">
          {formatDateShortYear(project.expected_finish)}
        </b>
        {/* Without a forecast there is no margin either - one dash, not two. */}
        {project.expected_finish === null ? null : (
          <span
            className={cn(
              'tabular text-[15px] font-semibold',
              margin === null ? 'text-tv2-muted' : margin < 0 ? 'text-tv2-coral' : 'text-tv2-green',
            )}
          >
            {margin === null ? '—' : `${formatSigned(margin)} d`}
          </span>
        )}
      </div>
      <p className="tabular text-[20px] font-bold">{formatInteger(project.open_change_requests)}</p>
      <div className="flex min-w-0 items-center gap-2.5">
        <span className={cn('h-3 w-3 shrink-0 rounded-full', status.dot)} aria-hidden />
        <div className="flex min-w-0 flex-col leading-[1.15]">
          <b className={cn('text-[17px] font-semibold', status.text)}>{status.label}</b>
          <span className="truncate text-[13px] text-tv2-soft">{project.status_reason}</span>
        </div>
      </div>
    </div>
  );
}

export function Box({
  title,
  aside,
  warn = false,
  className,
  children,
}: {
  title: string;
  aside?: ReactNode;
  warn?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <section
      className={cn(
        'flex min-h-0 flex-col gap-2.5 rounded-[10px] border-t-[3px] bg-tv2-surface px-5 py-3.5',
        warn ? 'border-tv2-amber' : 'border-tv2-accent',
        className,
      )}
    >
      <div className="flex items-baseline justify-between gap-3">
        <p className="whitespace-nowrap text-[14px] font-semibold tracking-[2px] text-tv2-soft">
          {title}
        </p>
        {aside}
      </div>
      {children}
    </section>
  );
}

function RoomBox({ capacity }: { capacity: TvProjectsResponse['capacity'] }) {
  const ready = capacity.ready_week;
  return (
    <Box title="PLADS TIL NYT PROJEKT" className="justify-between">
      {ready ? (
        <div>
          <p className="text-[56px] font-bold leading-[0.95] text-tv2-green">Uge {ready.week}</p>
          <p className="mt-1.5 text-[15px] text-tv2-soft">
            {formatInteger(ready.free_hours)} t ledige · et projekt kræver{' '}
            {formatNumber(capacity.project_hours, 1)} t
          </p>
        </div>
      ) : (
        <p className="text-[40px] font-bold leading-none text-tv2-coral">Ikke inden for perioden</p>
      )}
      <div className="flex gap-[18px] border-t border-tv2-divider-strong pt-2.5">
        <div>
          <b className="tabular block text-[24px] font-bold">
            {formatPercent(capacity.booked_pct_4_weeks)}
          </b>
          <span className="text-[12px] text-tv2-muted">booket næste 4 uger</span>
        </div>
        <div>
          <b className="tabular block text-[24px] font-bold">
            {formatNumber(capacity.free_hours_4_weeks, 1)} t
          </b>
          <span className="text-[12px] text-tv2-muted">ledigt næste 4 uger</span>
        </div>
      </div>
    </Box>
  );
}

function WeeksBox({ capacity }: { capacity: TvProjectsResponse['capacity'] }) {
  const { weeks, ready_week: ready, project_hours: need } = capacity;
  const isReady = (week: { year: number; week: number }) =>
    ready !== null && ready.year === week.year && ready.week === week.week;
  const first = weeks[0];
  const last = weeks.at(-1);

  return (
    <Box
      title="TEAMETS LEDIGE TIMER PR. UGE"
      className="justify-center"
      aside={
        first && last ? (
          <span className="text-[13px] text-tv2-muted">
            næste {weeks.length} uger · uge {first.week}–{last.week}
          </span>
        ) : null
      }
    >
      <div
        className="grid items-center gap-1 text-[13px]"
        style={{ gridTemplateColumns: `112px repeat(${Math.max(1, weeks.length)}, minmax(0,1fr))` }}
      >
        <span className="text-tv2-muted">Uge</span>
        {weeks.map((week) => (
          <span
            key={`u${week.year}-${week.week}`}
            className={cn(
              'text-center font-semibold',
              isReady(week) ? 'text-tv2-ink' : 'text-tv2-muted',
            )}
          >
            {week.week}
          </span>
        ))}

        <RowLabel title="Teamet" note="ledigt efter alt" />
        {weeks.map((week) => (
          <span
            key={`t${week.year}-${week.week}`}
            className={cn(
              'tabular flex h-[34px] items-center justify-center rounded-[5px] text-[15px] font-bold text-tv2-on-color',
              week.team_free <= 0
                ? 'bg-tv2-coral'
                : week.team_free < need
                  ? 'bg-tv2-amber'
                  : 'bg-tv2-green',
              isReady(week) && 'border-[3px] border-tv2-ink',
            )}
          >
            {formatInteger(Math.max(0, Math.round(week.team_free)))}
          </span>
        ))}

        <RowLabel title="Ikke tildelt" note="trukket fra teamet" amber />
        {weeks.map((week) => (
          <span
            key={`i${week.year}-${week.week}`}
            className="tabular flex h-[26px] items-center justify-center rounded-[5px] bg-tv2-bg font-semibold text-tv2-amber"
          >
            {week.unassigned > 0 ? formatSigned(-Math.round(week.unassigned)) : '—'}
          </span>
        ))}
      </div>
      <div className="flex flex-wrap gap-x-[18px] gap-y-1.5 text-[12px] text-tv2-muted">
        <LegendSquare className="bg-tv2-green">
          Plads til nyt projekt (≥ {formatNumber(need, 1)} t)
        </LegendSquare>
        <LegendSquare className="bg-tv2-amber">Delvist booket</LegendSquare>
        <LegendSquare className="bg-tv2-coral">Fuldt booket</LegendSquare>
      </div>
    </Box>
  );
}

function RowLabel({
  title,
  note,
  amber = false,
}: {
  title: string;
  note: string;
  amber?: boolean;
}) {
  return (
    <span className="flex flex-col leading-[1.15]">
      <b className={cn('text-[15px] font-semibold', amber && 'text-tv2-amber')}>{title}</b>
      <span className="text-[11px] text-tv2-muted">{note}</span>
    </span>
  );
}

function LegendSquare({ className, children }: { className: string; children: ReactNode }) {
  return (
    <span className="flex items-center gap-1.5">
      <i className={cn('inline-block h-[11px] w-[11px] rounded-[2px]', className)} />
      {children}
    </span>
  );
}

function UnassignedBox({
  unassigned,
}: {
  unassigned: TvProjectsResponse['capacity']['unassigned'];
}) {
  return (
    <Box title="IKKE TILDELT · MÅL 0" warn>
      <div className="flex items-baseline gap-2.5">
        <b className="tabular text-[38px] font-bold leading-none text-tv2-amber">
          {formatNumber(unassigned.hours, 1)}
        </b>
        <span className="text-[20px] font-medium">timer</span>
        <span className="ml-auto text-[15px] text-tv2-soft">
          {formatInteger(unassigned.tasks)} {unassigned.tasks === 1 ? 'opgave' : 'opgaver'} uden
          ansvarlig
        </span>
      </div>
      <div className="flex flex-col gap-[5px]">
        {unassigned.boards.map((board) => (
          <div
            key={board.name}
            className="grid grid-cols-[minmax(0,1fr)_70px_70px] gap-2 border-b border-tv2-divider py-px text-[13px]"
          >
            <span className="truncate">{board.name}</span>
            <span className="text-right text-tv2-muted">{formatInteger(board.tasks)} opg.</span>
            <span className="tabular text-right font-semibold">
              {formatNumber(board.hours, 1)} t
            </span>
          </div>
        ))}
      </div>
    </Box>
  );
}

function TopCustomersBox({ top }: { top: TvTopCustomers }) {
  const month = formatMonthName(top.month_start).toUpperCase();
  return (
    <Box title={`TOP KUNDER · ${month}`}>
      {top.customers.length === 0 ? (
        <p className="flex flex-1 items-center text-[15px] text-tv2-soft">
          Ingen timer på fakturaer endnu
        </p>
      ) : (
        <div className="flex flex-col gap-1.5">
          {top.customers.map((customer) => (
            <div
              key={customer.name}
              className="relative h-[22px] overflow-hidden rounded bg-tv2-bg"
            >
              <i
                className="absolute inset-y-0 left-0 bg-tv2-bar"
                style={{ width: `${Math.min(100, Math.max(0, customer.pct))}%` }}
              />
              <div className="relative flex h-[22px] items-center justify-between px-2.5 text-[13px]">
                <span className="truncate font-semibold">{customer.name}</span>
                <b className="tabular font-bold">{formatPercent(customer.pct)}</b>
              </div>
            </div>
          ))}
        </div>
      )}
      <p className="mt-auto text-[12px] text-tv2-muted">
        {formatInteger(top.total_hours)} timer i alt · {formatInteger(top.customer_count)}{' '}
        {top.customer_count === 1 ? 'kunde' : 'kunder'}
      </p>
    </Box>
  );
}
